import * as React from "react";
import {
    ActionButton,
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    ITag,
    Label,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Stack,
    TagItemSuggestion,
    TagPicker,
    Text,
    TextField,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { copilotPromptMetaStyle } from "./CopilotPromptList";
import { CopilotPromptAudience, CopilotPromptLanguage, ICopilotPromptInput } from "../../data-structures/interfaces";
import {
    COPILOT_PROMPT_LIMITS,
    CopilotPromptInputError,
    cleanCopilotPromptInput,
    librarySuggestions,
    validateCopilotPromptInput,
} from "../../utilities/helpers/copilotPrompts";

export interface CopilotPromptEditorProps {
    /** A global prompt also has an audience. */
    isGlobal: boolean;
    /** "New prompt" rather than "Edit prompt". */
    isNew: boolean;
    /** The values the form starts from. */
    initial: ICopilotPromptInput;
    /** Names offered for the default libraries; any other name can be typed. */
    libraryNames: string[];
    /** The global editor: the template configurations each offered name belongs to, by lowercase
     * name, shown after it in the suggestions (2026-10-21); a name without any shows alone. */
    libraryConfigurations?: Record<string, string[]>;
    isSaving: boolean;
    /** Shown above the buttons when the API refused the save. */
    errorMessage?: string;
    /** Called with the cleaned input once it passes validateCopilotPromptInput. */
    onSave: (input: ICopilotPromptInput) => void;
    onCancel: () => void;
}

const ALL_LANGUAGES = "all";
const LIBRARIES_LABEL_ID = "copilot-prompt-libraries-label";

const counter = (value: string | null | undefined, max: number): string => `${(value ?? "").length} / ${max}`;
const toTags = (names: string[]): ITag[] => names.map(name => ({ key: name, name }));

/**
 * The form for one Copilot prompt (spec 2026-10-13-copilot-chat-design.md §4 phase 3): title and
 * text with character counters, language, "Only when a location is selected", default libraries
 * suggested from the template configurations' libraries (a tenant without one: its sites'), the
 * audience for global prompts, and the guidance under Advanced.
 */
export const CopilotPromptEditor: React.FC<CopilotPromptEditorProps> = ({
    isGlobal,
    isNew,
    initial,
    libraryNames,
    libraryConfigurations,
    isSaving,
    errorMessage,
    onSave,
    onCancel,
}) => {
    const strings = React.useContext(LanguageContext);
    const [input, setInput] = React.useState<ICopilotPromptInput>(initial);
    const [showAdvanced, setShowAdvanced] = React.useState<boolean>(!!initial.guidance);
    const [errors, setErrors] = React.useState<CopilotPromptInputError[]>([]);
    // The library name typed in the picker but not picked yet: Fluent's picker never commits it
    // on its own, so Save adds it (audit 2026-10-19).
    const [typedLibrary, setTypedLibrary] = React.useState<string>("");

    const update = (change: Partial<ICopilotPromptInput>) => setInput(current => ({ ...current, ...change }));
    const errorFor = (field: CopilotPromptInputError, message: string) => (errors.indexOf(field) >= 0 ? message : undefined);

    const languageOptions: IDropdownOption[] = [
        { key: ALL_LANGUAGES, text: strings.CopilotPromptLanguageAll },
        { key: "en", text: strings.CopilotPromptLanguageEn },
        { key: "nl", text: strings.CopilotPromptLanguageNl },
        { key: "de", text: strings.CopilotPromptLanguageDe },
    ];
    const audienceOptions: IDropdownOption[] = [
        { key: 1, text: strings.CopilotPromptAudienceAccountancy },
        { key: 0, text: strings.CopilotPromptAudienceAll },
    ];

    // The typed name goes into what is checked and saved, never into the chips: a refused name stays
    // in the box, where it can be corrected (final review 2026-10-19). As a chip it came back from
    // the box on every Save, even after the chip was removed.
    // A typed name that names a known library takes that library's spelling (review 2026-10-19).
    const save = () => {
        const typedText = typedLibrary.trim();
        const typed = libraryNames.find(name => name.toLowerCase() === typedText.toLowerCase()) ?? typedText;
        const withTyped = typed && !input.defaultLibraries.some(name => name.toLowerCase() === typed.toLowerCase())
            ? { ...input, defaultLibraries: input.defaultLibraries.concat([typed]) }
            : input;
        const found = validateCopilotPromptInput(withTyped, isGlobal);
        setErrors(found);
        if (found.indexOf("guidance") >= 0) {
            setShowAdvanced(true);
        }
        if (found.length === 0) {
            onSave(cleanCopilotPromptInput(withTyped, isGlobal));
        }
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={() => { if (!isSaving) onCancel(); }}
            dialogContentProps={{
                type: DialogType.normal,
                title: isNew ? strings.CopilotPromptNew : strings.CopilotPromptEdit,
                closeButtonAriaLabel: strings.Close,
            }}
            modalProps={{ isBlocking: true }}
            minWidth={560}
            maxWidth={640}
        >
            <Stack tokens={{ childrenGap: 12 }}>
                <TextField
                    label={strings.Title}
                    required
                    value={input.title}
                    maxLength={COPILOT_PROMPT_LIMITS.title}
                    description={counter(input.title, COPILOT_PROMPT_LIMITS.title)}
                    errorMessage={errorFor("title", strings.CopilotPromptErrorTitle)}
                    onChange={(_, value) => update({ title: value ?? "" })}
                />
                <TextField
                    label={strings.CopilotPromptText}
                    required
                    multiline
                    rows={4}
                    value={input.text}
                    maxLength={COPILOT_PROMPT_LIMITS.text}
                    description={counter(input.text, COPILOT_PROMPT_LIMITS.text)}
                    errorMessage={errorFor("text", strings.CopilotPromptErrorText)}
                    onChange={(_, value) => update({ text: value ?? "" })}
                />
                <Dropdown
                    label={strings.CopilotPromptLanguage}
                    options={languageOptions}
                    selectedKey={input.language ?? ALL_LANGUAGES}
                    onChange={(_, option) => update({
                        language: option && option.key !== ALL_LANGUAGES ? (option.key as CopilotPromptLanguage) : null,
                    })}
                />
                {isGlobal && (
                    <Dropdown
                        label={strings.CopilotPromptAudience}
                        options={audienceOptions}
                        selectedKey={input.audience ?? null}
                        errorMessage={errorFor("audience", strings.CopilotPromptErrorAudience)}
                        onChange={(_, option) => update({ audience: option ? (option.key as CopilotPromptAudience) : null })}
                    />
                )}
                <Checkbox
                    label={strings.CopilotPromptRequiresScope}
                    checked={input.requiresScope}
                    onChange={(_, checked) => update({ requiresScope: !!checked })}
                />
                <Stack tokens={{ childrenGap: 4 }}>
                    <Label id={LIBRARIES_LABEL_ID}>{strings.CopilotPromptDefaultLibraries}</Label>
                    <TagPicker
                        selectedItems={toTags(input.defaultLibraries)}
                        onResolveSuggestions={(filter, selected) =>
                            toTags(librarySuggestions(filter, libraryNames, (selected ?? []).map(tag => tag.name)))}
                        onEmptyResolveSuggestions={selected =>
                            toTags(librarySuggestions("", libraryNames, (selected ?? []).map(tag => tag.name)))}
                        // typedLibrary follows the box alone: picking a suggestion clears the box
                        // (onInputChange("")), while removing a chip leaves its text in place.
                        // A suggestion names the configurations that have it; the chip and what is
                        // saved are the name alone.
                        onRenderSuggestionsItem={tag => {
                            const configurations = libraryConfigurations?.[tag.name.toLowerCase()] ?? [];
                            return (
                                <TagItemSuggestion>
                                    {tag.name}
                                    {configurations.length > 0 && <span className={copilotPromptMetaStyle}>{` · ${configurations.join(", ")}`}</span>}
                                </TagItemSuggestion>
                            );
                        }}
                        onChange={items => update({ defaultLibraries: (items ?? []).map(tag => tag.name) })}
                        onInputChange={value => {
                            setTypedLibrary(value ?? "");
                            return value;
                        }}
                        pickerSuggestionsProps={{
                            suggestionsHeaderText: strings.CopilotPromptLibrarySuggestions,
                            noResultsFoundText: strings.CopilotPromptNoLibraries,
                        }}
                        inputProps={{ "aria-labelledby": LIBRARIES_LABEL_ID, placeholder: strings.CopilotPromptLibrariesPlaceholder }}
                        itemLimit={COPILOT_PROMPT_LIMITS.libraries}
                    />
                    <Text variant="small">{strings.CopilotPromptDefaultLibrariesInfo}</Text>
                    {errors.indexOf("libraries") >= 0 && (
                        <MessageBar messageBarType={MessageBarType.error}>{strings.CopilotPromptErrorLibraries}</MessageBar>
                    )}
                </Stack>
                {/* At the left, its chevron in line with the fields: a vertical Stack stretches its
                    children, and a stretched button centres its text (Paul, 2026-10-16). */}
                <ActionButton
                    styles={{ root: { alignSelf: "flex-start", padding: 0 }, icon: { marginLeft: 0 } }}
                    iconProps={{ iconName: showAdvanced ? "ChevronDown" : "ChevronRight" }}
                    aria-expanded={showAdvanced}
                    onClick={() => setShowAdvanced(value => !value)}
                >
                    {strings.CopilotPromptAdvanced}
                </ActionButton>
                {showAdvanced && (
                    <TextField
                        label={strings.CopilotPromptGuidance}
                        multiline
                        rows={6}
                        value={input.guidance ?? ""}
                        maxLength={COPILOT_PROMPT_LIMITS.guidance}
                        description={`${strings.CopilotPromptGuidanceInfo} ${counter(input.guidance, COPILOT_PROMPT_LIMITS.guidance)}`}
                        errorMessage={errorFor("guidance", strings.CopilotPromptErrorGuidance)}
                        onChange={(_, value) => update({ guidance: value ?? "" })}
                    />
                )}
                {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
            </Stack>
            <DialogFooter>
                <PrimaryButton text={strings.Save} disabled={isSaving} onClick={save} />
                <DefaultButton text={strings.Cancel} disabled={isSaving} onClick={onCancel} />
            </DialogFooter>
        </Dialog>
    );
};
